/*
 * Copyright (c) 2023. American Express Company
 */

import React, { useCallback } from 'react';

import { IconCheck } from '@americanexpress/dls-react';
import Circle from 'src/components/atomic/atoms/circle/Circle';
import { OnboardingProgressViewModel } from 'src/types/view-models/onboarding/onboardingProgress.view.model';

import bgColors from 'src/styles/bgColors.module.scss';
import styles from './onboardingProgress.module.scss';

const ConnectorSolid = (): JSX.Element => <div className={styles.connectorSolid} />;

const ConnectorDashed = (): JSX.Element => <div className={styles.connectorDashed} />;

const CircleComplete = (): JSX.Element => (
    <Circle diameter="24px" className={bgColors.brand}>
        <IconCheck size="sm" className={styles.iconCheck} />
    </Circle>
);

const CircleIndex = (): JSX.Element => (
    <Circle diameter="24px" className={styles.circleIndex}>
        <Circle diameter="18px" className={styles.circleIndex} />
    </Circle>
);

const CircleTodo = (): JSX.Element => <Circle diameter="18px" className={styles.circleTodo} />;

export default function OnboardingProgress({ index, length }: OnboardingProgressViewModel): JSX.Element {
    if (index < 0 || length < index)
        console.error(
            `OnboardingProgress Invalid Props: must have 0 <= index and index <= length. Got index:${index} and length:${length}`
        );

    const Bar = useCallback(() => {
        const _bar: JSX.Element[] = [
            index === 0 ? <CircleIndex key="progress-circle-0" /> : <CircleComplete key="progress-circle-0" />
        ];
        for (let i = 1; i < length; i++) {
            if (i < index)
                _bar.push(
                    <ConnectorSolid key={`progress-connector-${i}`} />,
                    <CircleComplete key={`progress-circle-${i}`} />
                );
            else if (i === index)
                _bar.push(
                    <ConnectorSolid key={`progress-connector-${i}`} />,
                    <CircleIndex key={`progress-circle-${i}`} />
                );
            else
                _bar.push(
                    <ConnectorDashed key={`progress-connector-${i}`} />,
                    <CircleTodo key={`progress-circle-${i}`} />
                );
        }
        return <>{_bar}</>;
    }, [index, length]);

    return (
        <div className={styles.onboardingProgress}>
            <Bar />
        </div>
    );
}
